/**
 * Localized overrides for the bundled blog posts in src/lib/blog-posts.ts.
 *
 * Same lookup-with-fallback pattern as info-page-translations.ts: when a
 * translation exists for the visitor's locale, the renderer uses it;
 * otherwise it falls back to the Dutch source.
 *
 * Keys are blog post slugs. Each entry has 5 non-NL locale variants —
 * `nl` is intentionally absent because the source itself is Dutch.
 */

type Locale = "de" | "en" | "fr" | "es" | "it";

interface BlogPostTranslation {
  title: string;
  excerpt: string;
  content: string;
}

type SlugTranslations = Partial<Record<Locale, BlogPostTranslation>>;

export const BLOG_POST_TRANSLATIONS: Record<string, SlugTranslations> = {
  "hoe-breng-je-foundation-aan": {
    de: {
      title: "Wie trägt man Foundation auf?",
      excerpt: "Lernen Sie Schritt für Schritt, wie Sie hypoallergene Foundation für einen ebenmäßigen, natürlichen Teint auftragen. Geeignet für empfindliche Haut.",
      content: `
      <p>Foundation ist die Basis jedes Make-up-Looks. Wenn Sie jedoch empfindliche Haut haben, möchten Sie sicher sein, dass Ihre Foundation keine Reizungen verursacht. In diesem Tutorial zeigen wir, wie Sie unsere hypoallergene Foundation für das beste Ergebnis auftragen.</p>

      <h2>Vorbereitung</h2>
      <p>Beginnen Sie immer mit einem sauberen, mit Feuchtigkeit versorgten Gesicht. Tragen Sie zuerst Ihre Tagescreme auf und lassen Sie sie gut einziehen, bevor Sie die Foundation auftragen. Das sorgt für eine glatte Basis und verhindert, dass die Foundation in trockene Stellen einzieht.</p>

      <h2>Den richtigen Farbton wählen</h2>
      <p>Testen Sie den Farbton immer am Kieferrand, nicht an der Hand. Ihre Foundation sollte nahtlos in Ihre Haut übergehen. Bei Unity Cosmetics bieten wir mehrere Töne an, die speziell für ein natürliches Ergebnis entwickelt wurden.</p>

      <h2>Auftragen</h2>
      <p>Tragen Sie kleine Punkte Foundation auf Stirn, Wangen, Nase und Kinn auf. Verblenden Sie die Foundation mit einem Schwämmchen oder Pinsel von der Mitte des Gesichts nach außen. Arbeiten Sie in dünnen Schichten für eine aufbaubare Deckkraft.</p>

      <h2>Tipp für empfindliche Haut</h2>
      <p>Verwenden Sie immer saubere Pinsel und Schwämmchen. Schmutzige Tools können Bakterien enthalten, die Reizungen verursachen. Waschen Sie Ihre Pinsel mindestens einmal pro Woche mit einer milden Seife.</p>
    `,
    },
    en: {
      title: "How do you apply foundation?",
      excerpt: "Learn step by step how to apply hypoallergenic foundation for an even, natural complexion. Suitable for sensitive skin.",
      content: `
      <p>Foundation is the basis of every make-up look. But if you have sensitive skin, you want to be sure that your foundation doesn't cause irritation. In this tutorial we show you how to apply our hypoallergenic foundation for the best result.</p>

      <h2>Preparation</h2>
      <p>Always start with a clean, hydrated face. Apply your day cream first and let it absorb properly before applying foundation. This creates a smooth base and prevents the foundation from settling into dry patches.</p>

      <h2>Choosing the right shade</h2>
      <p>Always test the colour along your jawline, not on your hand. Your foundation should blend seamlessly into your skin. At Unity Cosmetics we offer several shades developed specifically for a natural result.</p>

      <h2>Applying</h2>
      <p>Apply small dots of foundation to your forehead, cheeks, nose and chin. Blend the foundation with a sponge or brush from the centre of your face outwards. Build up in thin layers for buildable coverage.</p>

      <h2>Tip for sensitive skin</h2>
      <p>Always use clean brushes and sponges. Dirty tools can contain bacteria that cause irritation. Wash your brushes at least once a week with a mild soap.</p>
    `,
    },
    fr: {
      title: "Comment appliquer le fond de teint ?",
      excerpt: "Apprenez étape par étape à appliquer un fond de teint hypoallergénique pour un teint uniforme et naturel. Adapté aux peaux sensibles.",
      content: `
      <p>Le fond de teint est la base de tout maquillage. Mais si vous avez la peau sensible, vous voulez être sûre que votre fond de teint ne provoque pas d'irritation. Dans ce tutoriel, nous vous montrons comment appliquer notre fond de teint hypoallergénique pour un résultat optimal.</p>

      <h2>Préparation</h2>
      <p>Commencez toujours par un visage propre et hydraté. Appliquez d'abord votre crème de jour et laissez-la bien pénétrer avant d'appliquer le fond de teint. Cela permet d'obtenir une base lisse et évite que le fond de teint ne marque les zones sèches.</p>

      <h2>Choisir la bonne teinte</h2>
      <p>Testez toujours la couleur le long de la mâchoire, pas sur la main. Votre fond de teint doit se fondre parfaitement dans votre peau. Chez Unity Cosmetics, nous proposons plusieurs teintes spécialement conçues pour un résultat naturel.</p>

      <h2>Application</h2>
      <p>Déposez de petits points de fond de teint sur le front, les joues, le nez et le menton. Estompez avec une éponge ou un pinceau, en partant du centre du visage vers l'extérieur. Travaillez en fines couches pour une couvrance modulable.</p>

      <h2>Conseil pour les peaux sensibles</h2>
      <p>Utilisez toujours des pinceaux et des éponges propres. Des outils sales peuvent contenir des bactéries qui provoquent des irritations. Lavez vos pinceaux au moins une fois par semaine avec un savon doux.</p>
    `,
    },
    es: {
      title: "¿Cómo aplicar la base de maquillaje?",
      excerpt: "Aprende paso a paso cómo aplicar una base de maquillaje hipoalergénica para un tono uniforme y natural. Apta para pieles sensibles.",
      content: `
      <p>La base de maquillaje es la base de cualquier look. Pero si tienes la piel sensible, querrás asegurarte de que tu base no provoque irritación. En este tutorial te mostramos cómo aplicar nuestra base hipoalergénica para conseguir el mejor resultado.</p>

      <h2>Preparación</h2>
      <p>Empieza siempre con la cara limpia e hidratada. Aplica primero tu crema de día y deja que se absorba bien antes de poner la base. Esto crea una superficie lisa y evita que la base se acumule en zonas secas.</p>

      <h2>Elegir el tono adecuado</h2>
      <p>Prueba siempre el color en la línea de la mandíbula, no en la mano. Tu base debe fundirse de forma natural con tu piel. En Unity Cosmetics ofrecemos varios tonos desarrollados especialmente para un resultado natural.</p>

      <h2>Aplicación</h2>
      <p>Aplica pequeños puntos de base en la frente, las mejillas, la nariz y la barbilla. Difumina con una esponja o un pincel desde el centro del rostro hacia fuera. Trabaja en capas finas para una cobertura modulable.</p>

      <h2>Consejo para piel sensible</h2>
      <p>Utiliza siempre pinceles y esponjas limpios. Las herramientas sucias pueden contener bacterias que provoquen irritación. Lava tus pinceles al menos una vez por semana con un jabón suave.</p>
    `,
    },
    it: {
      title: "Come si applica il fondotinta?",
      excerpt: "Impara passo dopo passo come applicare un fondotinta ipoallergenico per un incarnato uniforme e naturale. Adatto alla pelle sensibile.",
      content: `
      <p>Il fondotinta è la base di ogni make-up look. Ma se hai la pelle sensibile, vuoi essere sicura che il tuo fondotinta non provochi irritazione. In questo tutorial ti mostriamo come applicare il nostro fondotinta ipoallergenico per ottenere il miglior risultato.</p>

      <h2>Preparazione</h2>
      <p>Inizia sempre con un viso pulito e idratato. Applica prima la tua crema da giorno e lasciala assorbire bene prima di applicare il fondotinta. Questo crea una base liscia ed evita che il fondotinta si accumuli nelle zone secche.</p>

      <h2>Scegliere la tonalità giusta</h2>
      <p>Prova sempre il colore lungo la linea della mandibola, non sulla mano. Il tuo fondotinta deve fondersi senza stacchi con la tua pelle. Da Unity Cosmetics offriamo diverse tonalità sviluppate appositamente per un risultato naturale.</p>

      <h2>Applicazione</h2>
      <p>Applica piccoli punti di fondotinta su fronte, guance, naso e mento. Sfuma con una spugnetta o un pennello dal centro del viso verso l'esterno. Lavora a strati sottili per una coprenza modulabile.</p>

      <h2>Consiglio per la pelle sensibile</h2>
      <p>Usa sempre pennelli e spugnette puliti. Strumenti sporchi possono contenere batteri che causano irritazione. Lava i tuoi pennelli almeno una volta a settimana con un sapone delicato.</p>
    `,
    },
  },

  "hoe-breng-je-oogpotlood-aan": {
    de: {
      title: "Wie trägt man Augenstift auf?",
      excerpt: "Tipps und Techniken für das Auftragen von Augenstift ohne Reizung. Ideal für empfindliche Augen und Kontaktlinsenträgerinnen.",
      content: `
      <p>Augenstift kann Ihren Blick komplett verändern. Aber für Menschen mit empfindlichen Augen ist es wichtig, die richtige Technik zu verwenden. In diesem Video-Tutorial teilen wir unsere besten Tipps.</p>

      <h2>Der richtige Stift</h2>
      <p>Wählen Sie einen hypoallergenen Augenstift, der frei von Parfüm und reizenden Stoffen ist. Unsere Augenstifte sind speziell für die empfindliche Haut rund um die Augen entwickelt und für Kontaktlinsenträgerinnen geeignet.</p>

      <h2>Technik</h2>
      <p>Halten Sie Ihr Augenlid leicht gespannt und tragen Sie den Stift in kleinen, kurzen Strichen entlang des Wimpernrandes auf. Beginnen Sie am äußeren Augenwinkel und arbeiten Sie nach innen. Für einen subtilen Effekt können Sie die Linie mit einem kleinen Pinsel verwischen.</p>

      <h2>Wasserlinie</h2>
      <p>Möchten Sie den Stift auf der Wasserlinie auftragen? Achten Sie darauf, dass der Stift frisch angespitzt ist, aus Hygienegründen. Ziehen Sie das untere Augenlid vorsichtig nach unten und tragen Sie den Stift mit sanften Bewegungen auf.</p>

      <h2>Langanhaltendes Ergebnis</h2>
      <p>Für eine längere Haltbarkeit können Sie eine dünne Schicht Lidschatten in derselben Farbe über den Stift auftragen. Das fixiert die Farbe und verhindert ein Verlaufen.</p>
    `,
    },
    en: {
      title: "How do you apply eye pencil?",
      excerpt: "Tips and techniques for applying eye pencil without irritation. Ideal for sensitive eyes and contact lens wearers.",
      content: `
      <p>Eye pencil can completely transform your gaze. But for people with sensitive eyes, it's important to use the right technique. In this video tutorial we share our best tips.</p>

      <h2>The right pencil</h2>
      <p>Choose a hypoallergenic eye pencil that's free from fragrance and irritating substances. Our eye pencils are specially developed for the sensitive skin around the eyes and suitable for contact lens wearers.</p>

      <h2>Technique</h2>
      <p>Hold your eyelid lightly taut and apply the pencil in small, short strokes along the lash line. Start at the outer corner of your eye and work inwards. For a subtle effect, you can smudge the line with a small brush.</p>

      <h2>Waterline</h2>
      <p>Want to apply the pencil on your waterline? Make sure the pencil is freshly sharpened for hygiene. Gently pull your lower eyelid down and apply the pencil with soft movements.</p>

      <h2>Long-lasting result</h2>
      <p>For longer wear, apply a thin layer of eye shadow in the same colour over the pencil. This sets the colour and prevents smudging.</p>
    `,
    },
    fr: {
      title: "Comment appliquer le crayon à yeux ?",
      excerpt: "Conseils et techniques pour appliquer un crayon à yeux sans irritation. Idéal pour les yeux sensibles et les porteuses de lentilles.",
      content: `
      <p>Le crayon à yeux peut transformer complètement votre regard. Mais pour les personnes ayant les yeux sensibles, il est important d'utiliser la bonne technique. Dans ce tutoriel vidéo, nous partageons nos meilleurs conseils.</p>

      <h2>Le bon crayon</h2>
      <p>Choisissez un crayon à yeux hypoallergénique, sans parfum ni substances irritantes. Nos crayons à yeux sont spécialement conçus pour la peau sensible du contour des yeux et adaptés aux porteuses de lentilles de contact.</p>

      <h2>Technique</h2>
      <p>Tendez légèrement votre paupière et appliquez le crayon par petits traits courts le long de la ligne des cils. Commencez par le coin externe de l'œil et avancez vers l'intérieur. Pour un effet subtil, vous pouvez estomper la ligne avec un petit pinceau.</p>

      <h2>Muqueuse</h2>
      <p>Vous souhaitez appliquer le crayon sur la muqueuse ? Veillez à ce que le crayon soit fraîchement taillé pour une meilleure hygiène. Tirez doucement la paupière inférieure vers le bas et appliquez le crayon avec des mouvements doux.</p>

      <h2>Tenue longue durée</h2>
      <p>Pour une meilleure tenue, appliquez une fine couche de fard à paupières de la même couleur sur le crayon. Cela fixe la couleur et empêche les bavures.</p>
    `,
    },
    es: {
      title: "¿Cómo aplicar el lápiz de ojos?",
      excerpt: "Consejos y técnicas para aplicar el lápiz de ojos sin irritación. Ideal para ojos sensibles y portadoras de lentillas.",
      content: `
      <p>El lápiz de ojos puede transformar por completo tu mirada. Pero para personas con ojos sensibles, es importante usar la técnica adecuada. En este tutorial en vídeo compartimos nuestros mejores consejos.</p>

      <h2>El lápiz adecuado</h2>
      <p>Elige un lápiz de ojos hipoalergénico, libre de perfume y sustancias irritantes. Nuestros lápices de ojos se han desarrollado especialmente para la piel sensible del contorno de ojos y son aptos para portadoras de lentillas.</p>

      <h2>Técnica</h2>
      <p>Mantén el párpado ligeramente tenso y aplica el lápiz con pequeños trazos cortos a lo largo de la línea de las pestañas. Empieza por el extremo exterior del ojo y avanza hacia dentro. Para un efecto sutil, puedes difuminar la línea con un pincel pequeño.</p>

      <h2>Línea de agua</h2>
      <p>¿Quieres aplicar el lápiz en la línea de agua? Asegúrate de que el lápiz esté recién afilado por higiene. Baja con cuidado el párpado inferior y aplica el lápiz con movimientos suaves.</p>

      <h2>Resultado de larga duración</h2>
      <p>Para una mayor duración, aplica una fina capa de sombra de ojos del mismo color sobre el lápiz. Esto fija el color y evita que se corra.</p>
    `,
    },
    it: {
      title: "Come si applica la matita per occhi?",
      excerpt: "Consigli e tecniche per applicare la matita per occhi senza irritazione. Ideale per occhi sensibili e per chi porta le lenti a contatto.",
      content: `
      <p>La matita per occhi può trasformare completamente il tuo sguardo. Ma per chi ha occhi sensibili è importante usare la tecnica giusta. In questo video tutorial condividiamo i nostri migliori consigli.</p>

      <h2>La matita giusta</h2>
      <p>Scegli una matita per occhi ipoallergenica, priva di profumo e sostanze irritanti. Le nostre matite per occhi sono sviluppate appositamente per la pelle sensibile del contorno occhi e adatte a chi porta le lenti a contatto.</p>

      <h2>Tecnica</h2>
      <p>Tieni la palpebra leggermente tesa e applica la matita con piccoli tratti brevi lungo l'attaccatura delle ciglia. Parti dall'angolo esterno dell'occhio e procedi verso l'interno. Per un effetto sottile puoi sfumare la linea con un piccolo pennello.</p>

      <h2>Rima interna</h2>
      <p>Vuoi applicare la matita sulla rima interna? Assicurati che la matita sia appena temperata, per ragioni di igiene. Tira delicatamente verso il basso la palpebra inferiore e applica la matita con movimenti morbidi.</p>

      <h2>Risultato a lunga tenuta</h2>
      <p>Per una maggiore tenuta, applica un sottile strato di ombretto dello stesso colore sopra la matita. Così si fissa il colore e si evita che sbavi.</p>
    `,
    },
  },

  "hoe-breng-je-concealer-aan": {
    de: {
      title: "Wie trägt man Concealer auf?",
      excerpt: "Erfahren Sie, wie Sie Concealer verwenden, um Unregelmäßigkeiten zu kaschieren – speziell für empfindliche Haut. Ohne Reizungen, mit natürlichem Ergebnis.",
      content: `
      <p>Concealer ist unverzichtbar, um dunkle Augenringe, Rötungen und Unregelmäßigkeiten zu kaschieren. Mit der richtigen Technik erzielen Sie ein frisches, natürliches Ergebnis, ohne dass es maskenhaft wirkt.</p>

      <h2>Wo trägt man Concealer auf?</h2>
      <p>Die häufigsten Stellen sind unter den Augen (gegen dunkle Ringe), rund um die Nase (gegen Rötungen) und auf eventuellen Unreinheiten. Sie können Concealer auch verwenden, um die Augenbrauen zu betonen.</p>

      <h2>Der richtige Farbton</h2>
      <p>Für unter die Augen: Wählen Sie einen Ton, der eine halbe Nuance heller ist als Ihre Foundation. Zum Kaschieren von Rötungen: Wählen Sie einen Ton, der genau Ihrem Hautton entspricht.</p>

      <h2>Auftragetechnik</h2>
      <p>Tragen Sie den Concealer in Form eines umgekehrten Dreiecks unter dem Auge auf. Klopfen Sie den Concealer sanft mit dem Ringfinger ein (er übt den geringsten Druck aus) oder mit einem kleinen Schwämmchen. Niemals reiben, immer klopfen.</p>

      <h2>Fixieren</h2>
      <p>Fixieren Sie den Concealer mit etwas transparentem Puder, um ein Verlaufen zu verhindern. Verwenden Sie einen flauschigen Pinsel und tragen Sie das Puder mit leichten, rollenden Bewegungen auf.</p>
    `,
    },
    en: {
      title: "How do you apply concealer?",
      excerpt: "Discover how to use concealer to disguise imperfections, especially for sensitive skin. Without irritation, with a natural result.",
      content: `
      <p>Concealer is essential for disguising dark circles, redness and imperfections. With the right technique, you achieve a fresh, natural result without it looking mask-like.</p>

      <h2>Where do you apply concealer?</h2>
      <p>The most common places are under the eyes (for dark circles), around the nose (for redness) and on any blemishes. You can also use concealer to highlight your brows.</p>

      <h2>The right shade</h2>
      <p>For under the eyes: choose a shade half a tone lighter than your foundation. For covering redness: choose a shade that matches your skin tone exactly.</p>

      <h2>Application technique</h2>
      <p>Apply the concealer in an inverted triangle under your eye. Tap it in gently with your ring finger (which exerts the least pressure) or a small sponge. Never rub, always tap.</p>

      <h2>Setting</h2>
      <p>Set your concealer with a tiny amount of translucent powder to prevent it from creasing. Use a fluffy brush and apply the powder with light, rolling movements.</p>
    `,
    },
    fr: {
      title: "Comment appliquer l'anti-cernes ?",
      excerpt: "Découvrez comment utiliser l'anti-cernes pour camoufler les imperfections, particulièrement pour les peaux sensibles. Sans irritation, avec un résultat naturel.",
      content: `
      <p>L'anti-cernes est indispensable pour camoufler les cernes, les rougeurs et les imperfections. Avec la bonne technique, vous obtenez un résultat frais et naturel sans effet masque.</p>

      <h2>Où appliquer l'anti-cernes ?</h2>
      <p>Les zones les plus courantes sont sous les yeux (pour les cernes), autour du nez (pour les rougeurs) et sur d'éventuelles imperfections. Vous pouvez également utiliser l'anti-cernes pour mettre en valeur vos sourcils.</p>

      <h2>La bonne teinte</h2>
      <p>Pour sous les yeux : choisissez une teinte une demi-nuance plus claire que votre fond de teint. Pour camoufler les rougeurs : choisissez une teinte qui correspond exactement à votre carnation.</p>

      <h2>Technique d'application</h2>
      <p>Appliquez l'anti-cernes en triangle inversé sous l'œil. Tapotez-le délicatement avec l'annulaire (qui exerce le moins de pression) ou avec une petite éponge. Ne jamais frotter, toujours tapoter.</p>

      <h2>Fixation</h2>
      <p>Fixez votre anti-cernes avec un peu de poudre translucide pour éviter qu'il ne marque les plis. Utilisez un pinceau pelucheux et appliquez la poudre par mouvements légers et roulants.</p>
    `,
    },
    es: {
      title: "¿Cómo aplicar el corrector?",
      excerpt: "Descubre cómo usar el corrector para disimular imperfecciones, especialmente para piel sensible. Sin irritación, con un resultado natural.",
      content: `
      <p>El corrector es imprescindible para disimular ojeras, rojeces e imperfecciones. Con la técnica adecuada conseguirás un resultado fresco y natural sin que parezca una máscara.</p>

      <h2>¿Dónde aplicar el corrector?</h2>
      <p>Las zonas más habituales son debajo de los ojos (para las ojeras), alrededor de la nariz (para las rojeces) y sobre cualquier imperfección. También puedes usar el corrector para resaltar las cejas.</p>

      <h2>El tono adecuado</h2>
      <p>Para debajo de los ojos: elige un tono medio tono más claro que tu base. Para cubrir rojeces: elige un tono que coincida exactamente con tu piel.</p>

      <h2>Técnica de aplicación</h2>
      <p>Aplica el corrector en forma de triángulo invertido bajo el ojo. Da pequeños toques con el dedo anular (que ejerce menos presión) o con una esponja pequeña. Nunca frotes, siempre da toques.</p>

      <h2>Fijación</h2>
      <p>Fija el corrector con un poco de polvo translúcido para evitar que se acumule en los pliegues. Usa un pincel suave y aplica el polvo con movimientos ligeros y giratorios.</p>
    `,
    },
    it: {
      title: "Come si applica il correttore?",
      excerpt: "Scopri come usare il correttore per camuffare le imperfezioni, soprattutto sulla pelle sensibile. Senza irritazione, con un risultato naturale.",
      content: `
      <p>Il correttore è indispensabile per camuffare occhiaie, rossori e imperfezioni. Con la tecnica giusta ottieni un risultato fresco e naturale, senza effetto maschera.</p>

      <h2>Dove applicare il correttore?</h2>
      <p>Le zone più comuni sono sotto gli occhi (per le occhiaie), attorno al naso (per i rossori) e su eventuali imperfezioni. Puoi usare il correttore anche per mettere in risalto le sopracciglia.</p>

      <h2>La tonalità giusta</h2>
      <p>Per sotto gli occhi: scegli una tonalità più chiara di mezzo tono rispetto al tuo fondotinta. Per coprire i rossori: scegli una tonalità che corrisponda esattamente al tuo incarnato.</p>

      <h2>Tecnica di applicazione</h2>
      <p>Applica il correttore a triangolo rovesciato sotto l'occhio. Picchietta delicatamente con l'anulare (che esercita meno pressione) o con una piccola spugnetta. Mai sfregare, sempre picchiettare.</p>

      <h2>Fissaggio</h2>
      <p>Fissa il correttore con un velo di cipria trasparente per evitare che si depositi nelle pieghe. Usa un pennello soffice e applica la cipria con movimenti leggeri e rotatori.</p>
    `,
    },
  },

  "hoe-breng-je-mascara-aan": {
    de: {
      title: "Wie trägt man Mascara auf?",
      excerpt: "Die beste Art, Mascara aufzutragen, wenn Sie empfindliche Augen haben oder Kontaktlinsen tragen. Ohne Klümpchen, ohne Reizung.",
      content: `
      <p>Mascara macht vielleicht den größten Unterschied in Ihrer Make-up-Routine. Aber für Menschen mit empfindlichen Augen oder Kontaktlinsen kann sie auch eine Quelle der Reizung sein. In diesem Tutorial lernen Sie, wie Sie unsere hypoallergene Mascara am besten auftragen.</p>

      <h2>Vorbereitung</h2>
      <p>Achten Sie darauf, dass Ihre Wimpern sauber und fettfrei sind. Wenn Sie Kontaktlinsen tragen, setzen Sie diese immer vor dem Auftragen der Mascara ein. So vermeiden Sie, dass Mascara-Partikel unter Ihre Linsen geraten.</p>

      <h2>Auftragen in drei Schritten</h2>
      <p>1. Beginnen Sie am Wimpernansatz und wackeln Sie das Bürstchen leicht von links nach rechts, während Sie nach oben ziehen. Das sorgt für Volumen am Ansatz.</p>
      <p>2. Ziehen Sie das Bürstchen in einer fließenden Bewegung zu den Spitzen für Länge.</p>
      <p>3. Tragen Sie eine zweite Schicht auf, bevor die erste vollständig getrocknet ist. Das verhindert Klümpchen.</p>

      <h2>Untere Wimpern</h2>
      <p>Halten Sie das Bürstchen senkrecht und verwenden Sie nur die Spitze, um die unteren Wimpern zu beschichten. Hier ist weniger mehr.</p>

      <h2>Tipps für Linsenträgerinnen</h2>
      <p>Wählen Sie immer eine Tubing-Mascara oder eine, die speziell für empfindliche Augen entwickelt wurde. Unsere Mascara ist ophthalmologisch getestet und sicher für Kontaktlinsenträgerinnen. Wechseln Sie Ihre Mascara alle drei Monate für optimale Hygiene.</p>
    `,
    },
    en: {
      title: "How do you apply mascara?",
      excerpt: "The best way to apply mascara if you have sensitive eyes or wear contact lenses. Without clumps, without irritation.",
      content: `
      <p>Mascara may make the biggest difference in your make-up routine. But for people with sensitive eyes or contact lenses, it can also be a source of irritation. In this tutorial you'll learn how to best apply our hypoallergenic mascara.</p>

      <h2>Preparation</h2>
      <p>Make sure your lashes are clean and grease-free. If you wear contact lenses, always put them in before applying mascara. This prevents mascara particles from getting under your lenses.</p>

      <h2>Apply in three steps</h2>
      <p>1. Start at the lash root and wiggle the wand gently from left to right while pulling upwards. This creates volume at the base.</p>
      <p>2. Pull the wand through in one smooth movement towards the tips for length.</p>
      <p>3. Apply a second coat before the first one is fully dry. This prevents clumping.</p>

      <h2>Lower lashes</h2>
      <p>Hold the wand vertically and use only the tip to coat your lower lashes. Less is more here.</p>

      <h2>Tips for lens wearers</h2>
      <p>Always choose a tubing mascara or one specifically developed for sensitive eyes. Our mascara is ophthalmologically tested and safe for contact lens wearers. Replace your mascara every three months for optimal hygiene.</p>
    `,
    },
    fr: {
      title: "Comment appliquer le mascara ?",
      excerpt: "La meilleure façon d'appliquer le mascara si vous avez les yeux sensibles ou si vous portez des lentilles. Sans paquets, sans irritation.",
      content: `
      <p>Le mascara fait peut-être la plus grande différence dans votre routine maquillage. Mais pour les personnes ayant les yeux sensibles ou portant des lentilles, il peut aussi être source d'irritation. Dans ce tutoriel, vous apprendrez à appliquer au mieux notre mascara hypoallergénique.</p>

      <h2>Préparation</h2>
      <p>Veillez à ce que vos cils soient propres et non gras. Si vous portez des lentilles, mettez-les toujours avant d'appliquer le mascara. Cela évite que des particules ne se glissent sous vos lentilles.</p>

      <h2>Application en trois étapes</h2>
      <p>1. Commencez à la racine des cils et faites osciller la brosse doucement de gauche à droite tout en remontant. Cela crée du volume à la base.</p>
      <p>2. Tirez la brosse en un seul mouvement fluide vers les pointes pour la longueur.</p>
      <p>3. Appliquez une deuxième couche avant que la première ne soit complètement sèche. Cela évite les paquets.</p>

      <h2>Cils inférieurs</h2>
      <p>Tenez la brosse à la verticale et utilisez uniquement le bout pour coiffer les cils inférieurs. Ici, moins c'est mieux.</p>

      <h2>Conseils pour les porteuses de lentilles</h2>
      <p>Choisissez toujours un mascara à effet gainant ou spécialement conçu pour les yeux sensibles. Notre mascara a été testé sous contrôle ophtalmologique et est compatible avec les lentilles de contact. Remplacez votre mascara tous les trois mois pour une hygiène optimale.</p>
    `,
    },
    es: {
      title: "¿Cómo aplicar la máscara de pestañas?",
      excerpt: "La mejor manera de aplicar la máscara de pestañas si tienes ojos sensibles o llevas lentillas. Sin grumos, sin irritación.",
      content: `
      <p>La máscara puede ser lo que más diferencia marca en tu rutina de maquillaje. Pero para personas con ojos sensibles o portadoras de lentillas, también puede ser fuente de irritación. En este tutorial aprenderás a aplicar nuestra máscara hipoalergénica de la mejor manera.</p>

      <h2>Preparación</h2>
      <p>Asegúrate de que tus pestañas estén limpias y libres de grasa. Si llevas lentillas, póntelas siempre antes de aplicar la máscara. Así evitas que partículas se metan debajo de tus lentillas.</p>

      <h2>Aplicación en tres pasos</h2>
      <p>1. Empieza desde la raíz de las pestañas y mueve el cepillo suavemente de izquierda a derecha mientras subes. Esto aporta volumen en la base.</p>
      <p>2. Lleva el cepillo en un movimiento fluido hasta las puntas para alargar.</p>
      <p>3. Aplica una segunda capa antes de que la primera se seque por completo. Esto evita los grumos.</p>

      <h2>Pestañas inferiores</h2>
      <p>Sujeta el cepillo en vertical y utiliza solo la punta para las pestañas inferiores. Aquí, menos es más.</p>

      <h2>Consejos para portadoras de lentillas</h2>
      <p>Elige siempre una máscara de tipo tubing o una desarrollada especialmente para ojos sensibles. Nuestra máscara está testada oftalmológicamente y es segura para portadoras de lentillas. Renueva la máscara cada tres meses para una higiene óptima.</p>
    `,
    },
    it: {
      title: "Come si applica la mascara?",
      excerpt: "Il modo migliore per applicare la mascara se hai gli occhi sensibili o porti le lenti a contatto. Senza grumi, senza irritazione.",
      content: `
      <p>La mascara forse fa la differenza più grande nella tua routine di make-up. Ma per chi ha occhi sensibili o porta lenti a contatto può anche essere fonte di irritazione. In questo tutorial impari come applicare al meglio la nostra mascara ipoallergenica.</p>

      <h2>Preparazione</h2>
      <p>Assicurati che le ciglia siano pulite e prive di residui oleosi. Se porti le lenti a contatto, indossale sempre prima di applicare la mascara. In questo modo eviti che particelle di mascara finiscano sotto le lenti.</p>

      <h2>Applicazione in tre passaggi</h2>
      <p>1. Parti dalla radice delle ciglia e muovi delicatamente lo scovolino da sinistra a destra mentre lo porti verso l'alto. Così crei volume alla base.</p>
      <p>2. Tira lo scovolino in un unico movimento fluido verso le punte per allungare.</p>
      <p>3. Applica una seconda passata prima che la prima sia completamente asciutta. Così eviti i grumi.</p>

      <h2>Ciglia inferiori</h2>
      <p>Tieni lo scovolino in verticale e usa solo la punta per le ciglia inferiori. Qui meno è meglio.</p>

      <h2>Consigli per chi porta le lenti</h2>
      <p>Scegli sempre una mascara di tipo tubing o sviluppata appositamente per occhi sensibili. La nostra mascara è oftalmologicamente testata e adatta a chi porta le lenti a contatto. Sostituisci la mascara ogni tre mesi per un'igiene ottimale.</p>
    `,
    },
  },

  "blauwgroene-oogschaduw-look": {
    de: {
      title: "Blau-grüner Lidschatten-Look",
      excerpt: "Schritt-für-Schritt-Tutorial für einen auffälligen blau-grünen Lidschatten-Look mit hypoallergenen Produkten. Sicher für empfindliche Augen.",
      content: `
      <p>Ein blau-grüner Lidschatten-Look ist perfekt, wenn Sie etwas mehr als Ihr alltägliches Make-up möchten. In diesem Tutorial zeigen wir, wie Sie mit unseren hypoallergenen Lidschatten einen auffälligen, aber tragbaren Look kreieren.</p>

      <h2>Was brauchen Sie?</h2>
      <p>Für diesen Look verwenden Sie eine helle Basisfarbe, einen blau-grünen Lidschatten für das bewegliche Lid und einen dunkleren Ton für die Lidfalte. Dazu einen Verblendpinsel und einen flachen Pinsel zum Auftragen.</p>

      <h2>Schritt 1: Basis</h2>
      <p>Tragen Sie einen hellen, matten Lidschatten als Basis über das gesamte Lid auf. Das hilft, die Farben besser zu verblenden und länger zu halten.</p>

      <h2>Schritt 2: Hauptfarbe</h2>
      <p>Tragen Sie den blau-grünen Lidschatten mit einem flachen Pinsel auf das bewegliche Lid auf. Tupfen Sie die Farbe für maximale Farbabgabe statt zu wischen.</p>

      <h2>Schritt 3: Lidfalte</h2>
      <p>Tragen Sie einen etwas dunkleren Ton mit einem Verblendpinsel in die Lidfalte ein. Machen Sie Scheibenwischer-Bewegungen, damit die Farben nahtlos ineinander übergehen.</p>

      <h2>Schritt 4: Finish</h2>
      <p>Highlighten Sie den inneren Augenwinkel und die Mitte des Lids mit einem hellen Schimmer. Das gibt dem Look Tiefe. Schließen Sie mit Mascara und gegebenenfalls einer Linie Augenstift ab.</p>
    `,
    },
    en: {
      title: "Blue-green eye shadow look",
      excerpt: "Step-by-step tutorial for a striking blue-green eye shadow look with hypoallergenic products. Safe for sensitive eyes.",
      content: `
      <p>A blue-green eye shadow look is perfect when you want something a bit more than your everyday make-up. In this tutorial we show you how to create a striking but wearable look with our hypoallergenic eye shadows.</p>

      <h2>What do you need?</h2>
      <p>For this look you'll use a light base colour, a blue-green eye shadow for the moving lid and a darker shade for the crease. Plus a blending brush and a flat brush to apply.</p>

      <h2>Step 1: Base</h2>
      <p>Apply a light, matte eye shadow as a base across the whole lid. This helps the colours blend better and last longer.</p>

      <h2>Step 2: Main colour</h2>
      <p>Apply the blue-green eye shadow on the moving lid with a flat brush. Press the colour in for maximum colour payoff rather than swiping.</p>

      <h2>Step 3: Crease</h2>
      <p>Apply a slightly darker shade in your crease with a blending brush. Use windscreen-wiper motions so the colours blend seamlessly into each other.</p>

      <h2>Step 4: Finish</h2>
      <p>Highlight the inner eye corner and the centre of your lid with a light shimmer. This adds dimension to the look. Finish with mascara and optionally a line of eye pencil.</p>
    `,
    },
    fr: {
      title: "Maquillage des yeux bleu-vert",
      excerpt: "Tutoriel étape par étape pour un maquillage des yeux bleu-vert audacieux avec des produits hypoallergéniques. Sûr pour les yeux sensibles.",
      content: `
      <p>Un maquillage des yeux bleu-vert est parfait lorsque vous voulez aller un peu plus loin que votre maquillage quotidien. Dans ce tutoriel, nous vous montrons comment créer un look audacieux mais portable avec nos fards à paupières hypoallergéniques.</p>

      <h2>De quoi avez-vous besoin ?</h2>
      <p>Pour ce look, vous utilisez une couleur claire de base, un fard à paupières bleu-vert pour la paupière mobile et une teinte plus foncée pour le creux de l'œil. Avec un pinceau d'estompage et un pinceau plat pour l'application.</p>

      <h2>Étape 1 : Base</h2>
      <p>Appliquez un fard à paupières clair et mat en base sur toute la paupière. Cela aide les couleurs à mieux se fondre et à mieux tenir.</p>

      <h2>Étape 2 : Couleur principale</h2>
      <p>Appliquez le fard bleu-vert sur la paupière mobile avec un pinceau plat. Tapotez la couleur pour un rendu maximal plutôt que de la balayer.</p>

      <h2>Étape 3 : Creux de l'œil</h2>
      <p>Appliquez une teinte légèrement plus foncée dans le creux de l'œil avec un pinceau d'estompage. Faites des mouvements en essuie-glace pour que les couleurs se fondent parfaitement.</p>

      <h2>Étape 4 : Finition</h2>
      <p>Illuminez le coin interne de l'œil et le centre de la paupière avec un shimmer clair. Cela apporte de la dimension au look. Terminez avec un mascara et éventuellement un trait de crayon à yeux.</p>
    `,
    },
    es: {
      title: "Look de sombra azul-verde",
      excerpt: "Tutorial paso a paso para un llamativo look de sombra azul-verde con productos hipoalergénicos. Seguro para ojos sensibles.",
      content: `
      <p>Un look de sombra azul-verde es perfecto cuando quieres algo más que tu maquillaje diario. En este tutorial te mostramos cómo crear un look llamativo pero llevable con nuestras sombras hipoalergénicas.</p>

      <h2>¿Qué necesitas?</h2>
      <p>Para este look usarás un color claro de base, una sombra azul-verde para el párpado móvil y un tono más oscuro para la cuenca. Además, un pincel difuminador y un pincel plano para aplicar.</p>

      <h2>Paso 1: Base</h2>
      <p>Aplica una sombra clara y mate como base en todo el párpado. Esto ayuda a que los colores se fundan mejor y duren más tiempo.</p>

      <h2>Paso 2: Color principal</h2>
      <p>Aplica la sombra azul-verde en el párpado móvil con un pincel plano. Pulsa el color para conseguir la máxima intensidad, en lugar de arrastrar.</p>

      <h2>Paso 3: Cuenca</h2>
      <p>Aplica un tono ligeramente más oscuro en la cuenca con un pincel difuminador. Realiza movimientos de limpiaparabrisas para que los colores se fundan sin marcas.</p>

      <h2>Paso 4: Acabado</h2>
      <p>Ilumina el lagrimal y el centro del párpado con un brillo suave. Esto aporta dimensión al look. Termina con la máscara de pestañas y, opcionalmente, una línea con lápiz de ojos.</p>
    `,
    },
    it: {
      title: "Make-up occhi blu-verde",
      excerpt: "Tutorial passo dopo passo per un audace make-up occhi blu-verde con prodotti ipoallergenici. Sicuro per occhi sensibili.",
      content: `
      <p>Un make-up occhi blu-verde è perfetto quando vuoi qualcosa in più rispetto al tuo make-up di tutti i giorni. In questo tutorial ti mostriamo come creare un look d'effetto ma portabile con i nostri ombretti ipoallergenici.</p>

      <h2>Cosa ti serve?</h2>
      <p>Per questo look userai un colore chiaro come base, un ombretto blu-verde per la palpebra mobile e una tonalità più scura per la piega. Più un pennello da sfumatura e uno piatto per l'applicazione.</p>

      <h2>Passaggio 1: Base</h2>
      <p>Applica un ombretto chiaro e opaco come base su tutta la palpebra. Aiuta i colori a sfumare meglio e a durare più a lungo.</p>

      <h2>Passaggio 2: Colore principale</h2>
      <p>Applica l'ombretto blu-verde sulla palpebra mobile con un pennello piatto. Picchietta il colore per la massima resa, invece di passarlo a strisciare.</p>

      <h2>Passaggio 3: Piega</h2>
      <p>Applica una tonalità leggermente più scura nella piega con un pennello da sfumatura. Fai movimenti a "tergicristallo" per fondere i colori senza stacchi.</p>

      <h2>Passaggio 4: Finitura</h2>
      <p>Illumina l'angolo interno e il centro della palpebra con uno shimmer chiaro. Aggiunge profondità al look. Concludi con il mascara ed eventualmente con una linea di matita per occhi.</p>
    `,
    },
  },

  "roze-oogschaduw-look": {
    de: {
      title: "Rosa Lidschatten-Look",
      excerpt: "Kreieren Sie einen sanften, romantischen rosa Lidschatten-Look. Hypoallergen und parfümfrei, perfekt für empfindliche Haut.",
      content: `
      <p>Ein rosa Lidschatten-Look ist sanft, romantisch und überraschend tragbar. In diesem Tutorial lernen Sie, wie Sie einen wunderschönen rosa Look kreieren, der für empfindliche Augen geeignet ist.</p>

      <h2>Das richtige Rosa wählen</h2>
      <p>Rosa Lidschatten gibt es in vielen Varianten: von zartem Pastellrosa bis tiefem Mauve. Wählen Sie einen Ton, der zu Ihrem Hautton passt. Hellere Hauttöne sehen mit kühlen Rosatönen schön aus, während wärmere Hauttöne wunderschön mit Pfirsichrosa wirken.</p>

      <h2>Schritt 1: Primer</h2>
      <p>Beginnen Sie mit einer Lidschatten-Base, um die Farbe zu intensivieren und die Haltbarkeit zu verlängern. Das ist besonders wichtig bei helleren Farben, die sonst schnell verblassen können.</p>

      <h2>Schritt 2: Helle Basis</h2>
      <p>Tragen Sie ein helles Rosa über das gesamte Lid auf. Das wird Ihr Basisfarbton, auf dem Sie den Rest des Looks aufbauen.</p>

      <h2>Schritt 3: Vertiefung</h2>
      <p>Verwenden Sie ein tieferes Rosa oder Mauve in der Lidfalte für Definition. Verblenden Sie gut nach oben für einen sanften Übergang.</p>

      <h2>Schritt 4: Shimmer-Akzent</h2>
      <p>Tragen Sie einen rosa Schimmer in der Mitte des Lids und im inneren Augenwinkel auf. Das verleiht dem Look ein frisches, glamouröses Finish.</p>

      <h2>Tipp</h2>
      <p>Kombinieren Sie diesen Look mit einer nudefarbenen Lippenfarbe und subtilem Rouge für ein harmonisches Gesamtbild. Die rosa Töne in Ihrem Lidschatten werden so schön vom Rest Ihres Make-ups aufgenommen.</p>
    `,
    },
    en: {
      title: "Pink eye shadow look",
      excerpt: "Create a soft, romantic pink eye shadow look. Hypoallergenic and fragrance-free, perfect for sensitive skin.",
      content: `
      <p>A pink eye shadow look is soft, romantic and surprisingly wearable. In this tutorial you'll learn how to create a beautiful pink look that's suitable for sensitive eyes.</p>

      <h2>Choosing the right pink</h2>
      <p>Pink eye shadow comes in many varieties: from soft pastel pink to deep mauve. Choose a shade that suits your skin tone. Lighter skin tones look beautiful with cool pink shades, while warmer skin tones are stunning with peachy pinks.</p>

      <h2>Step 1: Primer</h2>
      <p>Start with an eye shadow base to intensify the colour and extend wear. This is especially important for lighter shades that can otherwise fade quickly.</p>

      <h2>Step 2: Light base</h2>
      <p>Apply a light pink across the whole lid. This becomes your base shade on which you build the rest of the look.</p>

      <h2>Step 3: Depth</h2>
      <p>Use a deeper pink or mauve in the crease for definition. Blend well upwards for a soft transition.</p>

      <h2>Step 4: Shimmer accent</h2>
      <p>Apply a pink shimmer to the centre of your lid and the inner eye corner. This gives the look a fresh, glamorous finish.</p>

      <h2>Tip</h2>
      <p>Pair this look with a nude lip colour and subtle blush for a harmonious whole. The pink tones in your eye shadow are then beautifully picked up by the rest of your make-up.</p>
    `,
    },
    fr: {
      title: "Maquillage des yeux rose",
      excerpt: "Créez un maquillage des yeux rose, doux et romantique. Hypoallergénique et sans parfum, parfait pour les peaux sensibles.",
      content: `
      <p>Un maquillage des yeux rose est doux, romantique et étonnamment portable. Dans ce tutoriel, vous apprendrez à créer un magnifique look rose adapté aux yeux sensibles.</p>

      <h2>Choisir le bon rose</h2>
      <p>Le fard à paupières rose se décline en de nombreuses variantes : du rose pastel doux au mauve profond. Choisissez une teinte qui convient à votre carnation. Les peaux plus claires sont sublimées par des roses froids, tandis que les peaux plus chaudes mettent en valeur les roses pêchés.</p>

      <h2>Étape 1 : Primer</h2>
      <p>Commencez par une base pour fards à paupières afin d'intensifier la couleur et d'allonger la tenue. C'est particulièrement important pour les teintes claires qui peuvent autrement s'estomper rapidement.</p>

      <h2>Étape 2 : Base claire</h2>
      <p>Appliquez un rose clair sur toute la paupière. Ce sera votre teinte de base sur laquelle vous construirez le reste du look.</p>

      <h2>Étape 3 : Profondeur</h2>
      <p>Utilisez un rose plus profond ou un mauve dans le creux de l'œil pour donner de la définition. Estompez bien vers le haut pour une transition douce.</p>

      <h2>Étape 4 : Accent shimmer</h2>
      <p>Appliquez un shimmer rose au centre de la paupière et dans le coin interne de l'œil. Cela donne au look une finition fraîche et glamour.</p>

      <h2>Conseil</h2>
      <p>Associez ce look à un rouge à lèvres nude et un blush subtil pour un ensemble harmonieux. Les tonalités roses de votre fard à paupières seront ainsi joliment reprises par le reste de votre maquillage.</p>
    `,
    },
    es: {
      title: "Look de sombra rosa",
      excerpt: "Crea un look de sombra rosa, suave y romántico. Hipoalergénico y sin perfume, perfecto para piel sensible.",
      content: `
      <p>Un look de sombra rosa es suave, romántico y sorprendentemente llevable. En este tutorial aprenderás a crear un precioso look rosa adecuado para ojos sensibles.</p>

      <h2>Elegir el rosa adecuado</h2>
      <p>La sombra rosa viene en muchas variantes: desde rosa pastel suave hasta malva profundo. Elige un tono que combine con tu piel. Los tonos de piel claros lucen preciosos con rosas fríos, mientras que los más cálidos resaltan con rosas melocotón.</p>

      <h2>Paso 1: Prebase</h2>
      <p>Empieza con una base de sombra de ojos para intensificar el color y prolongar la duración. Esto es especialmente importante en tonos claros, que de lo contrario pueden desvanecerse rápido.</p>

      <h2>Paso 2: Base clara</h2>
      <p>Aplica un rosa claro en todo el párpado. Será tu tono base sobre el que construirás el resto del look.</p>

      <h2>Paso 3: Profundidad</h2>
      <p>Usa un rosa o malva más intenso en la cuenca para dar definición. Difumina bien hacia arriba para conseguir una transición suave.</p>

      <h2>Paso 4: Toque de shimmer</h2>
      <p>Aplica un brillo rosa en el centro del párpado y en el lagrimal. Esto le da al look un acabado fresco y glamuroso.</p>

      <h2>Consejo</h2>
      <p>Combina este look con un labial nude y un colorete sutil para un conjunto armónico. Así los tonos rosados de tu sombra se realzan con el resto del maquillaje.</p>
    `,
    },
    it: {
      title: "Make-up occhi rosa",
      excerpt: "Crea un make-up occhi rosa, dolce e romantico. Ipoallergenico e senza profumo, perfetto per la pelle sensibile.",
      content: `
      <p>Un make-up occhi rosa è dolce, romantico e sorprendentemente versatile. In questo tutorial impari a creare un bellissimo look rosa adatto agli occhi sensibili.</p>

      <h2>Scegliere il rosa giusto</h2>
      <p>L'ombretto rosa esiste in molte varianti: dal rosa pastello delicato al malva intenso. Scegli una tonalità adatta al tuo incarnato. Gli incarnati chiari valorizzano molto i rosa freddi, mentre quelli caldi sono splendidi con i rosa pesca.</p>

      <h2>Passaggio 1: Primer</h2>
      <p>Inizia con una base per ombretti per intensificare il colore e prolungare la tenuta. È particolarmente importante con tonalità chiare, che altrimenti possono sbiadire in fretta.</p>

      <h2>Passaggio 2: Base chiara</h2>
      <p>Applica un rosa chiaro su tutta la palpebra. Diventerà la tua tonalità di base su cui costruisci il resto del look.</p>

      <h2>Passaggio 3: Profondità</h2>
      <p>Usa un rosa o malva più intenso nella piega per dare definizione. Sfuma bene verso l'alto per una transizione morbida.</p>

      <h2>Passaggio 4: Accento shimmer</h2>
      <p>Applica uno shimmer rosa al centro della palpebra e all'angolo interno dell'occhio. Questo regala al look una finitura fresca e glamour.</p>

      <h2>Consiglio</h2>
      <p>Abbina questo look a un rossetto nude e a un blush leggero per un risultato armonioso. Le note rosa dell'ombretto vengono così valorizzate dal resto del make-up.</p>
    `,
    },
  },

  "hoe-lang-is-make-up-houdbaar": {
    de: {
      title: "Wie lange ist Make-up haltbar?",
      excerpt: "Alles, was Sie über die Haltbarkeit von Make-up wissen müssen. Wann sollten Sie Produkte ersetzen und wie erkennen Sie abgelaufene Kosmetik?",
      content: `
      <p>Haben Sie sich jemals gefragt, wie lange Ihr Make-up eigentlich hält? Viele Menschen verwenden Produkte deutlich länger, als sicher ist. In diesem Artikel erklären wir, wie lange verschiedene Produkte haltbar sind und warum es wichtig ist, abgelaufenes Make-up zu ersetzen.</p>

      <h2>Das PAO-Symbol</h2>
      <p>Auf der Verpackung von Kosmetik finden Sie das PAO-Symbol (Period After Opening): ein Tiegel mit einer Zahl und dem Buchstaben M. „12M" bedeutet zum Beispiel, dass das Produkt 12 Monate nach Öffnung haltbar ist. Achten Sie immer darauf, wenn Sie ein neues Produkt öffnen.</p>

      <h2>Haltbarkeit pro Produkt</h2>

      <h3>Mascara: 3-6 Monate</h3>
      <p>Mascara hat die kürzeste Haltbarkeit aller Make-up-Produkte. Das dunkle, feuchte Milieu im Tubus ist ein Nährboden für Bakterien. Ersetzen Sie Ihre Mascara alle drei Monate, vor allem wenn Sie empfindliche Augen haben oder Kontaktlinsen tragen.</p>

      <h3>Foundation: 12-18 Monate</h3>
      <p>Flüssige Foundation ist nach dem Öffnen etwa ein bis anderthalb Jahre haltbar. Bemerken Sie, dass sich die Textur verändert, die Farbe abweicht oder das Produkt anders riecht? Zeit zum Ersetzen.</p>

      <h3>Concealer: 12 Monate</h3>
      <p>Vergleichbar mit Foundation, aber da Concealer oft direkt rund um die Augen verwendet wird, ist es besonders wichtig, ihn rechtzeitig zu ersetzen.</p>

      <h3>Lidschatten (Puder): 24 Monate</h3>
      <p>Puderprodukte sind länger haltbar, weil Bakterien in trockenen Umgebungen schlechter wachsen. Verwenden Sie aber immer saubere Pinsel.</p>

      <h3>Lippenstift und Lipgloss: 12-18 Monate</h3>
      <p>Lippenstift ist relativ lange haltbar. Achten Sie auf Veränderungen in Geruch, Textur oder Farbe als Zeichen, dass das Produkt abgelaufen ist.</p>

      <h3>Augen- und Lippenstift: 12-24 Monate</h3>
      <p>Da Sie Stifte regelmäßig spitzen, entfernen Sie immer wieder die äußere Schicht. Das macht sie länger haltbar als viele andere Produkte.</p>

      <h2>Anzeichen, dass Ihr Make-up abgelaufen ist</h2>
      <ul>
        <li>Veränderter Geruch (ranzig, chemisch)</li>
        <li>Veränderte Textur (klumpig, ausgetrocknet, Trennung der Öle)</li>
        <li>Veränderte Farbe</li>
        <li>Reizungen bei Anwendung, die Sie zuvor nicht hatten</li>
      </ul>

      <h2>Tipps für längere Haltbarkeit</h2>
      <p>Bewahren Sie Ihr Make-up an einem kühlen, trockenen Ort auf, vor direktem Sonnenlicht geschützt. Schließen Sie Produkte nach Gebrauch immer gut. Waschen Sie Ihre Hände, bevor Sie Produkte berühren, und halten Sie Ihre Pinsel und Schwämmchen sauber.</p>

      <h2>Warum das bei empfindlicher Haut besonders wichtig ist</h2>
      <p>Abgelaufene Kosmetik kann Bakterien und Pilze enthalten, die Hautreizungen, Infektionen und allergische Reaktionen verursachen können. Wenn Sie bereits empfindliche Haut haben, ist es besonders wichtig, Ihre Produkte rechtzeitig zu ersetzen und gute Hygiene einzuhalten.</p>
    `,
    },
    en: {
      title: "How long does make-up last?",
      excerpt: "Everything you need to know about make-up shelf life. When should you replace products and how do you recognise expired cosmetics?",
      content: `
      <p>Have you ever wondered how long your make-up actually lasts? Many people use products much longer than is safe. In this article we explain how long various products last and why it's important to replace expired make-up.</p>

      <h2>The PAO symbol</h2>
      <p>On cosmetic packaging you'll find the PAO symbol (Period After Opening): a jar with a number and the letter M. For example, "12M" means the product is good for 12 months after opening. Always pay attention to this when opening a new product.</p>

      <h2>Shelf life by product</h2>

      <h3>Mascara: 3-6 months</h3>
      <p>Mascara has the shortest shelf life of all make-up products. The dark, moist environment inside the tube is a breeding ground for bacteria. Replace your mascara every three months, especially if you have sensitive eyes or wear contact lenses.</p>

      <h3>Foundation: 12-18 months</h3>
      <p>Liquid foundation lasts about a year to a year and a half once opened. Notice the texture changing, the colour shifting or a different smell? Time to replace.</p>

      <h3>Concealer: 12 months</h3>
      <p>Similar to foundation, but because concealer is often used directly around the eyes, it's extra important to replace it on time.</p>

      <h3>Eye shadow (powder): 24 months</h3>
      <p>Powder products last longer because bacteria don't grow as easily in dry environments. Always use clean brushes though.</p>

      <h3>Lipstick and lip gloss: 12-18 months</h3>
      <p>Lipstick has a relatively long shelf life. Watch for changes in smell, texture or colour as a sign that the product has expired.</p>

      <h3>Eye and lip pencils: 12-24 months</h3>
      <p>Because you regularly sharpen pencils, you keep removing the outer layer. That makes them last longer than many other products.</p>

      <h2>Signs your make-up has expired</h2>
      <ul>
        <li>Changed smell (rancid, chemical)</li>
        <li>Changed texture (clumpy, dried out, oils separating)</li>
        <li>Changed colour</li>
        <li>Irritation on use that you didn't have before</li>
      </ul>

      <h2>Tips for longer shelf life</h2>
      <p>Store your make-up in a cool, dry place out of direct sunlight. Always close products properly after use. Wash your hands before touching products, and keep your brushes and sponges clean.</p>

      <h2>Why this matters even more for sensitive skin</h2>
      <p>Expired cosmetics can contain bacteria and fungi that may cause skin irritation, infections and allergic reactions. If you already have sensitive skin, it's especially important to replace products on time and maintain good hygiene.</p>
    `,
    },
    fr: {
      title: "Durée de conservation du maquillage",
      excerpt: "Tout ce que vous devez savoir sur la conservation du maquillage. Quand remplacer les produits et comment reconnaître des cosmétiques périmés ?",
      content: `
      <p>Vous êtes-vous déjà demandé combien de temps votre maquillage se conserve réellement ? Beaucoup de personnes utilisent des produits bien plus longtemps que ce qui est sûr. Dans cet article, nous expliquons combien de temps les différents produits se conservent et pourquoi il est important de remplacer le maquillage périmé.</p>

      <h2>Le symbole PAO</h2>
      <p>Sur l'emballage des cosmétiques, vous trouvez le symbole PAO (Period After Opening) : un pot avec un chiffre et la lettre M. Par exemple, « 12M » signifie que le produit se conserve 12 mois après ouverture. Soyez-y toujours attentive lorsque vous ouvrez un nouveau produit.</p>

      <h2>Durée de conservation par produit</h2>

      <h3>Mascara : 3 à 6 mois</h3>
      <p>Le mascara a la durée de conservation la plus courte de tous les produits de maquillage. L'environnement sombre et humide à l'intérieur du tube est un terreau pour les bactéries. Remplacez votre mascara tous les trois mois, surtout si vous avez les yeux sensibles ou si vous portez des lentilles de contact.</p>

      <h3>Fond de teint : 12 à 18 mois</h3>
      <p>Le fond de teint liquide se conserve environ un an à un an et demi après ouverture. Vous remarquez que la texture change, que la couleur évolue ou que le produit sent différemment ? Il est temps de le remplacer.</p>

      <h3>Anti-cernes : 12 mois</h3>
      <p>Comparable au fond de teint, mais comme l'anti-cernes est souvent utilisé directement autour des yeux, il est encore plus important de le remplacer à temps.</p>

      <h3>Fard à paupières (poudre) : 24 mois</h3>
      <p>Les produits en poudre se conservent plus longtemps car les bactéries se développent moins bien dans les environnements secs. Utilisez toujours des pinceaux propres.</p>

      <h3>Rouge à lèvres et gloss : 12 à 18 mois</h3>
      <p>Le rouge à lèvres a une durée de conservation relativement longue. Soyez attentive aux changements d'odeur, de texture ou de couleur comme signes que le produit est périmé.</p>

      <h3>Crayons à yeux et à lèvres : 12 à 24 mois</h3>
      <p>Comme vous taillez régulièrement vos crayons, vous retirez à chaque fois la couche extérieure. Cela les rend plus durables que beaucoup d'autres produits.</p>

      <h2>Signes que votre maquillage est périmé</h2>
      <ul>
        <li>Odeur modifiée (rance, chimique)</li>
        <li>Texture modifiée (granuleuse, desséchée, séparation des huiles)</li>
        <li>Couleur modifiée</li>
        <li>Irritation à l'utilisation que vous n'aviez pas auparavant</li>
      </ul>

      <h2>Conseils pour une meilleure conservation</h2>
      <p>Stockez votre maquillage dans un endroit frais et sec, à l'abri de la lumière directe du soleil. Refermez toujours bien les produits après usage. Lavez-vous les mains avant de toucher les produits et gardez vos pinceaux et éponges propres.</p>

      <h2>Pourquoi cela importe davantage pour les peaux sensibles</h2>
      <p>Les cosmétiques périmés peuvent contenir des bactéries et des champignons susceptibles de provoquer des irritations cutanées, des infections et des réactions allergiques. Si vous avez déjà la peau sensible, il est particulièrement important de remplacer vos produits à temps et de maintenir une bonne hygiène.</p>
    `,
    },
    es: {
      title: "¿Cuánto dura el maquillaje?",
      excerpt: "Todo lo que necesitas saber sobre la caducidad del maquillaje. ¿Cuándo deberías sustituir los productos y cómo reconocer cosmética caducada?",
      content: `
      <p>¿Te has preguntado alguna vez cuánto dura realmente tu maquillaje? Muchas personas usan los productos mucho más tiempo del que es seguro. En este artículo te explicamos cuánto duran los distintos productos y por qué es importante sustituir el maquillaje caducado.</p>

      <h2>El símbolo PAO</h2>
      <p>En el envase de la cosmética encontrarás el símbolo PAO (Period After Opening): un tarrito con un número y la letra M. Por ejemplo, "12M" significa que el producto se conserva 12 meses después de abierto. Fíjate siempre en él al abrir un producto nuevo.</p>

      <h2>Duración por producto</h2>

      <h3>Máscara de pestañas: 3-6 meses</h3>
      <p>La máscara tiene la duración más corta de todos los productos de maquillaje. El ambiente oscuro y húmedo del tubo es un caldo de cultivo para bacterias. Sustituye tu máscara cada tres meses, sobre todo si tienes ojos sensibles o llevas lentillas.</p>

      <h3>Base de maquillaje: 12-18 meses</h3>
      <p>La base líquida dura aproximadamente entre un año y un año y medio una vez abierta. ¿Notas que la textura cambia, el color se altera o el producto huele distinto? Es hora de sustituirla.</p>

      <h3>Corrector: 12 meses</h3>
      <p>Similar a la base, pero como el corrector se usa a menudo directamente alrededor de los ojos, es aún más importante sustituirlo a tiempo.</p>

      <h3>Sombra de ojos (polvo): 24 meses</h3>
      <p>Los productos en polvo duran más porque las bacterias crecen peor en ambientes secos. Aun así, usa siempre pinceles limpios.</p>

      <h3>Pintalabios y brillo: 12-18 meses</h3>
      <p>El pintalabios tiene una duración relativamente larga. Atenta a los cambios de olor, textura o color como señales de que el producto ha caducado.</p>

      <h3>Lápices de ojos y labios: 12-24 meses</h3>
      <p>Como sacas punta regularmente a los lápices, eliminas la capa exterior cada vez. Esto los hace duraderos en comparación con muchos otros productos.</p>

      <h2>Señales de que tu maquillaje ha caducado</h2>
      <ul>
        <li>Olor distinto (rancio, químico)</li>
        <li>Textura distinta (con grumos, reseca, separación de aceites)</li>
        <li>Color distinto</li>
        <li>Irritación al usarlo que antes no tenías</li>
      </ul>

      <h2>Consejos para una mayor duración</h2>
      <p>Guarda tu maquillaje en un lugar fresco y seco, lejos de la luz solar directa. Cierra siempre bien los productos después de usarlos. Lávate las manos antes de tocar los productos y mantén pinceles y esponjas limpios.</p>

      <h2>Por qué esto es aún más importante en piel sensible</h2>
      <p>Los cosméticos caducados pueden contener bacterias y hongos capaces de provocar irritación cutánea, infecciones y reacciones alérgicas. Si ya tienes la piel sensible, es especialmente importante sustituir los productos a tiempo y mantener una buena higiene.</p>
    `,
    },
    it: {
      title: "Quanto dura il make-up?",
      excerpt: "Tutto quello che devi sapere sulla durata del make-up. Quando sostituire i prodotti e come riconoscere i cosmetici scaduti?",
      content: `
      <p>Ti sei mai chiesta quanto dura davvero il tuo make-up? Molte persone utilizzano i prodotti per molto più tempo di quanto sia sicuro. In questo articolo ti spieghiamo quanto durano i diversi prodotti e perché è importante sostituire il make-up scaduto.</p>

      <h2>Il simbolo PAO</h2>
      <p>Sulle confezioni dei cosmetici trovi il simbolo PAO (Period After Opening): un vasetto con un numero e la lettera M. Ad esempio, "12M" significa che il prodotto si conserva 12 mesi dopo l'apertura. Controllalo sempre quando apri un prodotto nuovo.</p>

      <h2>Durata per prodotto</h2>

      <h3>Mascara: 3-6 mesi</h3>
      <p>La mascara è il prodotto di make-up con la durata più breve. L'ambiente scuro e umido all'interno del tubetto è un terreno fertile per i batteri. Sostituisci la mascara ogni tre mesi, soprattutto se hai occhi sensibili o porti lenti a contatto.</p>

      <h3>Fondotinta: 12-18 mesi</h3>
      <p>Il fondotinta liquido si conserva circa un anno o un anno e mezzo dopo l'apertura. Noti che la texture cambia, il colore vira o il prodotto ha un odore diverso? È ora di sostituirlo.</p>

      <h3>Correttore: 12 mesi</h3>
      <p>Simile al fondotinta, ma poiché il correttore viene spesso usato proprio intorno agli occhi, è particolarmente importante sostituirlo per tempo.</p>

      <h3>Ombretto (in polvere): 24 mesi</h3>
      <p>I prodotti in polvere durano più a lungo perché i batteri crescono meno bene in ambienti asciutti. Usa sempre, però, pennelli puliti.</p>

      <h3>Rossetto e lucidalabbra: 12-18 mesi</h3>
      <p>Il rossetto ha una durata relativamente lunga. Attenzione ai cambiamenti di odore, texture o colore come segno che il prodotto è scaduto.</p>

      <h3>Matite per occhi e labbra: 12-24 mesi</h3>
      <p>Poiché temperi regolarmente le matite, rimuovi ogni volta lo strato esterno. Questo le rende più durature di molti altri prodotti.</p>

      <h2>Segni che il tuo make-up è scaduto</h2>
      <ul>
        <li>Odore cambiato (rancido, chimico)</li>
        <li>Texture cambiata (granulosa, secca, separazione degli oli)</li>
        <li>Colore cambiato</li>
        <li>Irritazione all'uso che prima non avevi</li>
      </ul>

      <h2>Consigli per una maggiore durata</h2>
      <p>Conserva il make-up in un luogo fresco e asciutto, al riparo dalla luce solare diretta. Chiudi sempre bene i prodotti dopo l'uso. Lavati le mani prima di toccare i prodotti e mantieni pennelli e spugnette puliti.</p>

      <h2>Perché tutto questo è ancora più importante per la pelle sensibile</h2>
      <p>I cosmetici scaduti possono contenere batteri e funghi in grado di causare irritazione cutanea, infezioni e reazioni allergiche. Se hai già la pelle sensibile, è particolarmente importante sostituire i prodotti per tempo e mantenere una buona igiene.</p>
    `,
    },
  },
};

/** Returns translated title/excerpt/content for a post slug + locale, or
 *  null if the locale is Dutch or no translation exists. */
export function getTranslatedBlogPost(
  slug: string,
  locale: string,
): BlogPostTranslation | null {
  if (locale === "nl") return null;
  const variants = BLOG_POST_TRANSLATIONS[slug];
  if (!variants) return null;
  return variants[locale as Locale] ?? null;
}
